import { useEffect, useRef, useState, useCallback } from "react";
import { Search, X, Plus, Loader2, Globe, Landmark } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/** Admin Scanner Universe — search DHAN F&O (India) or TradingView (Global) and pick
 *  exactly which symbols the AI scanner spends credits on. */
export const UniverseManager = () => {
  const [uni, setUni] = useState({ indian: [], global: [] });
  const [market, setMarket] = useState("indian");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const tRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/universe");
      setUni({ indian: data.indian || [], global: data.global || [] });
    } catch (e) { console.error("universe load failed:", e?.message || e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    clearTimeout(tRef.current);
    if (market === "global" && !q.trim()) { setResults([]); return; }
    setLoading(true);
    tRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/admin/universe/search", { params: { q, market } });
        setResults(data || []);
      } catch { setResults([]); } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(tRef.current);
  }, [q, market]);

  const applyItems = (items) => {
    setUni({ indian: items.filter((i) => isIndian(i)), global: items.filter((i) => !isIndian(i)) });
    window.dispatchEvent(new Event("universe:changed"));
  };
  const inUni = (sym) => [...uni.indian, ...uni.global].some((i) => i.symbol === sym);

  const add = async (item) => {
    try {
      const { data } = await api.post("/admin/universe", {
        symbol: item.symbol, tv_symbol: item.tv_symbol, name: item.name, category: item.category || "Stock",
      });
      applyItems(data.items);
      toast.success(`${item.symbol} added — scanner picks it up on the next sweep`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add symbol");
    }
  };
  const remove = async (sym) => {
    try {
      const { data } = await api.delete(`/admin/universe/${encodeURIComponent(sym)}`);
      applyItems(data.items);
      toast(`${sym} removed — zero AI spend on it from now`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove symbol");
    }
  };

  const total = uni.indian.length + uni.global.length;
  return (
    <div className="mt-4 pt-3 border-t border-zinc-800" data-testid="ai-cc-universe">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div>
          <div className="text-[10px] text-zinc-300 font-medium">Scanner universe</div>
          <div className="text-[9px] text-zinc-600 font-mono">
            The AI scans ONLY these symbols · they are also every client's dashboard list · {total} active
            {total === 0 && <span className="text-[#F59E0B]"> · empty — scanner idle</span>}
          </div>
        </div>
        <div className="inline-flex border border-zinc-800">
          {[["indian", "India · DHAN F&O", Landmark], ["global", "Global · TradingView", Globe]].map(([m, label, Icon]) => (
            <button key={m} onClick={() => { setMarket(m); setResults([]); }}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 text-[9px] font-mono uppercase tracking-widest ${market === m ? "bg-[#007AFF] text-white" : "text-zinc-500 hover:text-white"}`}
                    data-testid={`universe-market-${m}`}>
              <Icon className="h-3 w-3" /> {label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div>
          <div className="flex items-center gap-2 border border-zinc-800 focus-within:border-[#007AFF] px-2 py-1.5">
            <Search className="h-3 w-3 text-zinc-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder={market === "indian" ? "Search NSE/BSE F&O — NIFTY, HDFCBANK, TATAMOTORS…" : "Search global — AAPL, NVDA, BTCUSD, XAUUSD…"}
                   className="flex-1 bg-transparent outline-none text-xs font-mono text-white placeholder:text-zinc-600"
                   data-testid="universe-search-input" />
            {loading && <Loader2 className="h-3 w-3 animate-spin text-zinc-500" />}
            {q && <button onClick={() => setQ("")} className="text-zinc-600 hover:text-white"><X className="h-3 w-3" /></button>}
          </div>
          <div className="mt-1 max-h-52 overflow-y-auto border border-zinc-900 divide-y divide-zinc-900" data-testid="universe-results">
            {results.length === 0 && !loading && (
              <div className="px-2 py-3 text-[10px] font-mono text-zinc-600">
                {market === "indian" ? "Type to filter the F&O universe (indices + F&O stocks only)." : "Type a ticker or company name."}
              </div>
            )}
            {results.map((r) => {
              const added = inUni(r.symbol);
              return (
                <div key={`${r.tv_symbol}-${r.symbol}`} className="flex items-center justify-between gap-2 px-2 py-1.5 hover:bg-white/[0.02]"
                     data-testid={`universe-result-${r.symbol}`}>
                  <div className="min-w-0">
                    <div className="text-xs font-mono text-white">{r.symbol}
                      <span className="ml-2 text-[9px] text-zinc-500">{r.exchange}{r.category ? ` · ${r.category}` : ""}{r.lot_size ? ` · lot ${r.lot_size}` : ""}</span>
                      {r.has_data === false && <span className="ml-2 text-[8px] px-1 border border-[#F59E0B]/40 text-[#F59E0B]">NO DHAN ID</span>}
                    </div>
                    <div className="text-[10px] text-zinc-500 truncate">{r.name}</div>
                  </div>
                  <button onClick={() => add(r)} disabled={added}
                          className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest border ${added ? "border-zinc-800 text-zinc-600" : "border-[#22C55E]/50 text-[#22C55E] hover:bg-[#22C55E]/10"}`}
                          data-testid={`universe-add-${r.symbol}`}>
                    <Plus className="h-3 w-3" /> {added ? "Added" : "Add"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="space-y-3">
          {[["Indian", uni.indian], ["Global", uni.global]].map(([label, list]) => (
            <div key={label}>
              <div className="text-[9px] uppercase tracking-widest text-zinc-600 font-mono mb-1">{label} · {list.length}</div>
              <div className="flex flex-wrap gap-1" data-testid={`universe-list-${label.toLowerCase()}`}>
                {list.length === 0 && <span className="text-[10px] font-mono text-zinc-700">none</span>}
                {list.map((i) => (
                  <span key={i.symbol} className="group inline-flex items-center border border-[#007AFF]/40 bg-[#007AFF]/10 text-[#3B9BFF] text-[10px] font-mono uppercase tracking-widest"
                        data-testid={`universe-chip-${i.symbol}`}>
                    <span className="px-2 py-1" title={i.name}>{i.symbol}</span>
                    <button onClick={() => remove(i.symbol)} className="px-1.5 text-zinc-600 hover:text-[#EF4444]" title={`Remove ${i.symbol}`}
                            data-testid={`universe-remove-${i.symbol}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const isIndian = (i) => {
  const s = (i.symbol || "").toUpperCase();
  const tv = (i.tv_symbol || "").toUpperCase();
  if (["NIFTY", "BANKNIFTY", "SENSEX", "FINNIFTY", "MIDCPNIFTY", "BANKEX"].includes(s)) return true;
  if (tv.startsWith("NSE:") || tv.startsWith("BSE:")) return true;
  return false;
};
